import { useMutation } from '@apollo/client/react';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import React, { useEffect, useState } from 'react';
import { Trans } from 'react-i18next';

import Content from '~/shared/components/content';
import FormBlock from '~/shared/components/form-block';
import { getFreeConsultationFormConfig } from '~/shared/configs/forms';
import { CONSULTATION_TYPE, PAGE_NAMES } from '~/shared/constants';
import { createOneConsultationMutation } from '~/shared/graphql-queries/consultations';
import { useAppDispatch } from '~/shared/store/hooks';
import { changeStateOfConfirmModal } from '~/shared/store/slices/app/app-slice';

import './contacts-request-form.scss';

import contactFormBg from '~/assets/backgrounds/contact-form-bg.webp';

const ContactsRequestForm = ({ t, type = CONSULTATION_TYPE.COURSE }) => {
  const formConfig = getFreeConsultationFormConfig({ t });
  const router = useRouter();
  const [isResetForm, setIsResetForm] = useState(false);
  const dispatch = useAppDispatch();
  const [consultationCreateMutation] = useMutation(createOneConsultationMutation);
  const onSubmit = async ({ userName, phone, entity = null }: { userName; phone }) => {
    dispatch(
      changeStateOfConfirmModal({
        onTrigger: async (itemKey) => {
          await consultationCreateMutation({
            variables: {
              record: {
                userName,
                type,
                entity,
                phone: `${phone?.countryCode}${phone?.areaCode}${phone?.phoneNumber}`,
              },
            },
          });
          setIsResetForm(true);
          router.push(PAGE_NAMES.THANK_YOU_FOR_SENDING_REQUEST);
        },
        title: t('modal.confirmation-contacts-request-form.title'),
        message: (
          <div className="styled-modal">
            <div className="contacts-request-form__content">
              <div className="contacts-request-form__description-container">
                {t('modal.confirmation-contacts-request-form.message', {
                  ns: ['modal'],
                })}
              </div>
              <div className="contacts-request-form__info">
                <div className="contacts-request-form__info-item">
                  <span className="contacts-request-form__info-item-label">
                    {t('text.name', {
                      ns: ['text'],
                    })}
                    :
                  </span>
                  <span>{userName}</span>
                </div>
                <div className="contacts-request-form__info-item">
                  <span className="contacts-request-form__info-item-label">
                    {t('text.phone', {
                      ns: ['text'],
                    })}
                    :
                  </span>
                  <span>{`+${phone?.countryCode}${phone?.areaCode}${phone?.phoneNumber}`}</span>
                </div>
              </div>
            </div>
          </div>
        ),
        isShowed: true,
      }),
    );
  };

  useEffect(() => {
    if (isResetForm) {
      setIsResetForm(false);
    }
  }, [isResetForm]);

  return (
    <Content className="contacts-request-form">
      <div className="contacts-request-form__content-block">
        <div className="contacts-request-form__description-block">
          <h3 className="contacts-request-form__description-title">{t('text.do-you-want-zero-lesson')}</h3>
          <div className="contacts-request-form__description">
            <Trans ns="text" components={{ br: <br /> }} i18nKey="text.our-manager-will-help-you" />
          </div>
        </div>
        <div className="contacts-request-form__form">
          {!isResetForm && (
            <FormBlock initialValues={formConfig.initialValues} formConfig={formConfig} onSubmit={onSubmit} />
          )}
        </div>
      </div>
      <Image
        loading="lazy"
        priority={false}
        className="contacts-request-form__bg-image"
        alt={t('text.do-you-want-zero-lesson')}
        title={t('text.do-you-want-zero-lesson')}
        src={contactFormBg.src}
        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 810, 810"
        width={810}
        height={689}
      />
    </Content>
  );
};

export default ContactsRequestForm;
